import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'gatsby';
import Dropdown from '@images/icons/section-navigation.svg';
import { isEDMode } from '@utils/helper';
/**
 * MobileDropdown component for use in mobile views
 * Can be used for both in-page section navigation and regular page navigation
 *
 * @param {Object} props - Component props
 * @param {Array} props.options - Array of option objects with id, label, and href/contentfulid properties
 * @param {string} props.activeOption - ID or value of the currently active option
 * @param {string} props.placeholder - Placeholder text for the dropdown
 * @param {string} props.style - Additional CSS classes for styling
 * @param {boolean} props.isInPageNavigation - Whether the dropdown is for in-page navigation
 * @param {Function} props.onOptionChange - Callback when option changes
 * @returns {React.ReactElement} MobileDropdown component
 */
const MobileDropdown = ({
	options = [],
	activeOption = '',
	placeholder = 'Jump to',
	style = '',
	isInPageNavigation = false,
	onOptionChange = () => {}
}) => {

	const [isOpen, setIsOpen] = useState(false);
	const [selectedOption, setSelectedOption] = useState(
		options.find(option =>
			isInPageNavigation
				? option.contentfulid === activeOption
				: option.id === activeOption
		) || null
	);
	const dropdownRef = useRef(null);
	const [isFixed, setIsFixed] = useState(false);
	const [initialPosition, setInitialPosition] = useState(null);


	// Update selected option when activeOption prop changes
	useEffect(() => {

		if (activeOption) {
			const option = options.find(option =>
				isInPageNavigation
					? option.contentfulid === activeOption
					: option.id === activeOption
			);

			if (option && (!selectedOption || (isInPageNavigation ? selectedOption.contentfulid !== activeOption : selectedOption.id !== activeOption))) {
				setSelectedOption(option);
			}
		}
	}, [activeOption, options, selectedOption, isInPageNavigation]);

	// Handle click outside to close dropdown
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Handle option selection
	const handleOptionSelect = (option) => {
		setSelectedOption(option);
		setIsOpen(false);
		onOptionChange(isInPageNavigation ? option.contentfulid : option.id);
	};


	useEffect(() => {
		const handleScroll = () => {
			if (!initialPosition) {
				setInitialPosition(dropdownRef.current?.getBoundingClientRect().top + window.scrollY);
				return;
			}

			const scrollPosition = window.scrollY;
			setIsFixed(scrollPosition > initialPosition - 20);
		};

		// Set initial position on mount
		if (dropdownRef.current) {
			setInitialPosition(dropdownRef.current.getBoundingClientRect().top + window.scrollY);
		}

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, [initialPosition]);

	return (
		<div
			ref={dropdownRef}
			className={`mobile-dropdown ${isFixed ? 'fixed left-0 right-0' : '-mt-6'} mx-8 transition-[top] duration-[1000ms] linear ${style}`}
		>
			{/* Dropdown trigger button */}
			<button
				className={`!w-full flex items-center px-6 py-4 rounded-full border-gradient-button bg-white/80 gap-4 mx-auto z-30 ${isInPageNavigation ? 'justify-center' : 'justify-start px-8'}`}
				onClick={() => setIsOpen(!isOpen)}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
			>
				<span className="text-p_medium font-bold text-dark_green">
					{selectedOption ? selectedOption.label : (isInPageNavigation || isEDMode ? "Jump to" : placeholder)}
				</span>
				<img src={Dropdown} alt="Dropdown" className={`w-[11px] h-2 absolute right-5 ${isOpen ? 'rotate-180' : ''}`} />
			</button>

			{/* Dropdown options */}
			{isOpen && (
				<div
					className="relative z-20 w-full h-0 -mt-5 bg-white rounded-bottom_32 shadow-lg"
					role="listbox"
				>
					<div className="absolute z-20 w-full pt-11 pb-8 bg-white rounded-bottom_32 shadow-lg flex flex-col gap-6">
					{options.map((option, index) => {
						// For in-page navigation options
						if (isInPageNavigation) {
							// Check if this option is the selected one by comparing labels
							// since contentfulid might not be available
							const isSelected = selectedOption &&
								selectedOption.label === option.label;

							return (
								<a
									key={option.contentfulid || `in-page-option-${index}`}
									className={`px-6 py-0 cursor-pointer text-dark_green text-center text-[14px] hover:font-bold ${
										isSelected ? 'font-bold' : 'font-normal'
									}`}
									onClick={() => handleOptionSelect(option)}
									role="option"
									aria-selected={isSelected}
									data-label={option.label}
									href={`#${option.contentfulid}`}
								>
									{option.label}
								</a>
							);
						}
						// For regular page navigation
						else {
							// Check if this option is the selected one
							const isSelected = selectedOption &&
								selectedOption.id === option.id;

							return (
								<Link
									key={option.id || `page-option-${index}`}
									to={option.href}
									className={`block text-dark_green text-[14px] px-8 hover:font-bold ${
										isSelected ? 'font-bold relative' : 'font-normal'
									}`}
									onClick={() => handleOptionSelect(option)}
									role="option"
									aria-selected={isSelected}
									data-label={option.label}
									href={option.contentfulid}
								>
									{isSelected &&
										<span className="absolute left-0 top-1/2 -translate-y-1/2 w-[24px] h-0.5 bg-orange"></span>
									}
									{option.label}
								</Link>
							);
						}
					})}
					</div>
				</div>
			)}
		</div>
	);
};

export default MobileDropdown;
